// ============================================================================
// Frontend Permission Catalog v2 — thin wrapper over permissionCatalog.data.json
// ============================================================================
//
// SOURCE OF TRUTH: functions/src/rbac/permissionCatalog.data.json.
// src/constants/permissionCatalog.data.json is a byte-identical copy of that
// file (the frontend build can't reach functions/), and the CI gate
// scripts/check-permission-catalog-sync.mjs byte-compares the two. To change a
// permission, edit the backend JSON and copy it here verbatim — never edit
// this wrapper's data by hand (RBAC-001: hand-mirrored copies drifted once).
// ============================================================================

import catalogData from './permissionCatalog.data.json';

export interface UIPermission {
  key: string;
  label: string;
  description?: string;
  /** short plain-words line for the permission grid row */
  summary?: string;
  /** keys this permission automatically includes */
  implies?: string[];
  assignable?: boolean;
  systemOnly?: boolean;
}

export interface UIPermissionCategory {
  /** stable category id, e.g. "account" — same value as `category` */
  id: string;
  category: string; // stable category id, e.g. "account"
  label: string; // human-readable, e.g. "account"
  blurb?: string;
  permissions: UIPermission[];
}

interface RawCatalogEntry {
  key: string;
  category: string;
  action: string;
  label: string;
  summary: string;
  description: string;
  implies?: string[];
  assignable: boolean;
  systemOnly: boolean;
  addedIn: string;
}

interface RawCatalog {
  version: number;
  categories: Array<{ id: string; label: string; blurb: string }>;
  permissions: RawCatalogEntry[];
  retired: Record<string, string | null>;
}

const raw = catalogData as unknown as RawCatalog;

export const CATALOG_VERSION_UI = raw.version;

/** Category metadata in catalog order. */
export const PERMISSION_CATEGORIES_UI: ReadonlyArray<{ id: string; label: string; blurb: string }> =
  raw.categories;

/** Catalog grouped by category; category order = JSON order. */
export const PERMISSION_CATALOG_UI: UIPermissionCategory[] = raw.categories.map((cat) => ({
  id: cat.id,
  category: cat.id,
  label: cat.label,
  blurb: cat.blurb,
  permissions: raw.permissions
    .filter((p) => p.category === cat.id)
    .map((p) => ({
      key: p.key,
      label: p.label,
      description: p.description,
      summary: p.summary,
      implies: p.implies ?? [],
      assignable: p.assignable,
      systemOnly: p.systemOnly,
    })),
}));

/** Flat set of every valid permission key for quick membership lookups. */
export const PERMISSION_KEYS_UI: Set<string> = new Set(raw.permissions.map((p) => p.key));

/** Flat array of every permission (used when the catalog needs to be iterated linearly). */
export const ALL_PERMISSIONS_UI: UIPermission[] = PERMISSION_CATALOG_UI.flatMap(
  (cat) => cat.permissions
);

// ── Internal partitions (catalog v4 developer.*, v5 phoneline.*) ─────────────
// Internal-partition keys render ONLY inside an enabled internal org
// (useDeveloperOrg — the server's rbacGetDeveloperOrgStatus mirror; enforcement
// is server-side). The role editor's main grid, search, and "N of M" footer
// draw from the STANDARD sets so a normal org's editor is pixel- and
// count-identical to pre-v4. Each partition is one catalog category rendered
// as its own locked section, guarded by its own delegate key.

export const DEVELOPER_CATEGORY_ID = 'developer';
export const PHONELINE_CATEGORY_ID = 'phoneline';

/** Category-id order here = render order of the internal sections. */
export const INTERNAL_CATEGORY_IDS: readonly string[] = [
  DEVELOPER_CATEGORY_ID,
  PHONELINE_CATEGORY_ID,
];

/** Per-partition delegate key — the permission that unlocks EDITING that
 * partition's checkboxes in the role editor (mirrors the server's
 * requireDeveloperDelegation / requirePhonelineDelegation guards). */
export const INTERNAL_CATEGORY_DELEGATE_KEY_UI: Readonly<Record<string, string>> = {
  [DEVELOPER_CATEGORY_ID]: 'developer.delegate',
  [PHONELINE_CATEGORY_ID]: 'phoneline.delegate',
};

const INTERNAL_PERMISSION_PREFIXES_UI: readonly string[] = ['developer.', 'phoneline.'];

export function isDeveloperPermissionKeyUI(key: string): boolean {
  return key.startsWith('developer.');
}

export function isInternalPermissionKeyUI(key: string): boolean {
  return INTERNAL_PERMISSION_PREFIXES_UI.some((prefix) => key.startsWith(prefix));
}

/** The developer category (with its permissions), or null if the catalog copy
 * predates v4. */
export const DEVELOPER_CATALOG_CATEGORY_UI: UIPermissionCategory | null =
  PERMISSION_CATALOG_UI.find((c) => c.id === DEVELOPER_CATEGORY_ID) ?? null;

/** The internal-partition categories present in this catalog copy, in
 * INTERNAL_CATEGORY_IDS order — what an internal org's editor renders as its
 * distinct locked sections. */
export const INTERNAL_CATALOG_CATEGORIES_UI: UIPermissionCategory[] =
  INTERNAL_CATEGORY_IDS.map((id) => PERMISSION_CATALOG_UI.find((c) => c.id === id))
    .filter((c): c is UIPermissionCategory => Boolean(c));

/** All developer.* keys. */
export const DEVELOPER_PERMISSION_KEYS_UI: Set<string> = new Set(
  raw.permissions.filter((p) => isDeveloperPermissionKeyUI(p.key)).map((p) => p.key)
);

/** All phoneline.* keys. */
export const PHONELINE_PERMISSION_KEYS_UI: Set<string> = new Set(
  raw.permissions.filter((p) => p.key.startsWith('phoneline.')).map((p) => p.key)
);

/** Every internal-partition key (all prefixes). */
export const INTERNAL_PERMISSION_KEYS_UI: Set<string> = new Set(
  raw.permissions.filter((p) => isInternalPermissionKeyUI(p.key)).map((p) => p.key)
);

/** Catalog minus the internal categories — what a NORMAL org's editor renders. */
export const STANDARD_PERMISSION_CATALOG_UI: UIPermissionCategory[] =
  PERMISSION_CATALOG_UI.filter((c) => !INTERNAL_CATEGORY_IDS.includes(c.id));

/** Every non-internal key — the normal-org "N of M" denominator (exactly the
 * pre-v4 catalog). */
export const STANDARD_PERMISSION_KEYS_UI: Set<string> = new Set(
  raw.permissions.filter((p) => !isInternalPermissionKeyUI(p.key)).map((p) => p.key)
);

/**
 * Internal keys the DEFAULT roles hold inside an internal org (owner directive
 * 2026-08-22: no extra roles — the sets live in the existing default roles).
 * Display mirror of the server's rbac/internalRoleDefaults.ts map, applied
 * through computed permissions rather than stored grants — the role editor
 * uses this so a system role's internal checkboxes show the TRUTH (checked +
 * locked) instead of the empty stored-grants view. A jest pin keeps this
 * byte-equal to the server map. Owner is absent on purpose (the owner card
 * already force-checks everything); read-only holds nothing.
 */
export const INTERNAL_TEMPLATE_DEFAULT_KEYS_UI: Readonly<Record<string, readonly string[]>> = {
  admin: Array.from(INTERNAL_PERMISSION_KEYS_UI),
  associate: [
    'developer.code.view',
    'developer.code.manage',
    'developer.deploys.view',
    'developer.logs.view',
    'developer.audit.view',
    'developer.cloud.view',
    'developer.fleet.view',
    'developer.escalations.view',
    'developer.triage.view',
    // v6 (2026-08-22): contributors run the prod-e2e suite.
    'developer.tests.view',
    'developer.tests.execute',
  ],
  paralegal: [
    'developer.code.view',
    'developer.deploys.view',
    'developer.logs.view',
    'developer.audit.view',
    'developer.cloud.view',
    'developer.fleet.view',
    'developer.escalations.view',
    'developer.triage.view',
    // v6 (2026-08-22): diagnostics read test-run state, never start runs.
    'developer.tests.view',
  ],
};

/**
 * Retired keys → current replacement (null = capability no longer exists).
 * Grants read off not-yet-migrated role docs may still carry old keys; the UI
 * uses this to display and save them as their current equivalents.
 */
export const RETIRED_PERMISSION_MAP_UI: Readonly<Record<string, string | null>> = raw.retired;

/**
 * The grants a NEW custom role starts with in the role editor — catalog v11
 * (parallel branches, 2026-09-08), v12 (voice in the chat, 2026-09-09) and v13
 * (organize your own thread list, 2026-09-13).
 * Every system role holds these by template; the custom-role editor is the ONE
 * place an owner or admin turns them off, so they must start checked (the
 * server's catalogMigration appends the same keys once to custom roles that
 * predate each stamp — CUSTOM_ROLE_DEFAULT_KEYS_V11 / _V12 / _V13).
 */
export const DEFAULT_CUSTOM_ROLE_GRANTS_UI: readonly string[] = [
  'tools.parallel-branches.use',
  'tools.chat-voice.use',
  'threads.organize-own',
  // Catalog v14 (2026-09-17): the shared prompt library's VIEW only. Publishing
  // and curating are deliberate grants — a new custom role should not be able
  // to write into a space the whole workspace reads without somebody choosing
  // that, and the migration's CUSTOM_ROLE_DEFAULT_KEYS_V14 makes the same cut.
  'prompts.library.view',
];
